import { useCallback, useEffect, useState } from "react";
import { GraduationCap, Loader2, RotateCcw, Search } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

export default function AdminStudents() {
  const { t, lang } = useT();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async (q = "") => {
    setLoading(true);
    setError("");
    try {
      const data = await adminApi.listStudents(q);
      setStudents(data.students || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => load(search), search ? 350 : 0);
    return () => clearTimeout(id);
  }, [search, load]);

  const resetOnboarding = async (s) => {
    if (!window.confirm(t("admin.students.resetConfirm", { name: s.name || s.email }))) return;
    setBusyId(s.id);
    try {
      await adminApi.resetStudentOnboarding(s.id);
      setStudents((prev) => prev.map((x) => (x.id === s.id ? { ...x, onboarding_completed: false } : x)));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = async (s, status) => {
    setBusyId(s.id);
    try {
      await adminApi.setMembershipStatus(s.id, status);
      setStudents((prev) => prev.map((x) => (x.id === s.id ? { ...x, membership_status: status } : x)));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

  return (
    <AdminShell>
      <div data-testid="admin-students-page" className="max-w-5xl">
        <div className="flex items-center gap-3 mb-2">
          <GraduationCap size={20} className="text-gold" strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-bone tracking-tight">{t("admin.students.title")}</h1>
        </div>
        <p className="text-sm text-bone/50 mb-8">{t("admin.students.subtitle")}</p>

        <div className="relative mb-6 max-w-md">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-bone/35" strokeWidth={1.5} />
          <input
            data-testid="admin-students-search"
            className="w-full bg-black/40 border border-white/10 focus:border-gold/60 outline-none pl-11 pr-4 py-3 text-bone text-sm placeholder:text-bone/30 transition-colors"
            placeholder={t("admin.students.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {error && <p data-testid="admin-students-error" className="text-sm text-red-400/90 mb-4">{error}</p>}

        {loading ? (
          <div className="py-16 text-center text-bone/40"><Loader2 className="animate-spin inline" size={20} /></div>
        ) : students.length === 0 ? (
          <p data-testid="admin-students-empty" className="text-sm text-bone/40 py-10">{t("admin.students.empty")}</p>
        ) : (
          <div className="border border-white/10 divide-y divide-white/5">
            {students.map((s) => (
              <div key={s.id} data-testid={`admin-student-row-${s.email}`} className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-bone truncate">{s.name}</p>
                  <p className="text-xs text-bone/45 truncate">{s.email}</p>
                </div>
                <span className="text-[10px] tracking-[0.16em] uppercase text-bone/45 w-28">
                  {s.membership_number || "—"}
                </span>
                <select
                  data-testid={`admin-student-status-${s.email}`}
                  value={s.membership_status || "aspirant"}
                  disabled={busyId === s.id}
                  onChange={(e) => setStatus(s, e.target.value)}
                  className="text-[10px] tracking-[0.14em] uppercase bg-black/40 border border-white/15 text-bone/70 px-2 py-1.5 outline-none hover:border-gold/50 disabled:opacity-40"
                >
                  <option value="aspirant">Aspirant (intro)</option>
                  <option value="founding_member">Founding member (full)</option>
                  <option value="member">Member (full)</option>
                  <option value="lifetime">Lifetime (full)</option>
                </select>
                <span className="text-xs text-bone/35 w-28 hidden md:block">{fmtDate(s.created_at)}</span>
                <span
                  data-testid={`admin-student-onboarding-${s.email}`}
                  className={`text-[10px] tracking-[0.18em] uppercase px-3 py-1 border ${
                    s.onboarding_completed
                      ? "border-gold/40 text-gold bg-gold/[0.06]"
                      : "border-white/15 text-bone/45"
                  }`}
                >
                  {s.onboarding_completed ? t("admin.students.done") : t("admin.students.pending")}
                </span>
                <button
                  data-testid={`admin-student-reset-${s.email}`}
                  onClick={() => resetOnboarding(s)}
                  disabled={!s.onboarding_completed || busyId === s.id}
                  className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-bone/70 hover:border-gold hover:text-gold disabled:opacity-25 disabled:hover:border-white/15 disabled:hover:text-bone/70 px-4 py-2 transition-colors"
                >
                  {busyId === s.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} strokeWidth={1.5} />}
                  {t("admin.students.reset")}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
